/**
 * Chat answer configuration + small imperative helpers.
 *
 * These are the env/URL-derived flags and one-shot helpers that the chat
 * orchestration (`useChat`) reads. They were extracted verbatim from App.tsx
 * (Task 201) so the shell stays thin; behavior is byte-for-byte unchanged.
 */
import type { MorphDirection } from "../components/MorphAnimation";

export const MAX_QUERY_LENGTH = 1000;

// Minimum time the first-query draw/charge is shown before the arrow lands, so
// fast or cached /answer responses still play the signature sequence instead of
// flashing past it (Task 074). When real latency exceeds this, the arrow simply
// holds/charges for the full wait — the network drives the duration.
export const MIN_DRAW_MS = 800;

/** How many prior turns to send for rewrite context (last 2 exchanges). */
export const FOLLOWUP_CONTEXT_TURNS = 4;

/**
 * Experimental hold-to-release send mode (Task 082). Enabled by ?send=hold
 * (canonical) or the tiny aliases ?hold=1 / ?release=hold. Default (no param) is
 * the normal click/Enter submit. Hold-to-release is UX friction, not abuse
 * protection — real public protection stays Tasks 057 (rate limits) / 060
 * (proxy) / 058 (cache/budget).
 */
export function readHoldToRelease(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return (
    params.get("send") === "hold" ||
    params.get("hold") === "1" ||
    params.get("release") === "hold"
  );
}

export function nextPendingId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function pickMorphDirection(): MorphDirection {
  // Test hooks (Tasks 080 + 084): ?morph=<dir> (URL, evaluated per submit) wins,
  // then VITE_FORCE_MORPH_DIRECTION (env, set at dev start). Either pins the
  // direction so the visual smoke can exercise Bowstring AND Aimed Shot
  // deterministically against one running server. Unset in production → the
  // 50/50 split (the duality payoff, see the design handoff).
  if (typeof window !== "undefined") {
    const urlDir = new URLSearchParams(window.location.search).get("morph");
    if (urlDir === "bowstring" || urlDir === "aimed-shot") {
      return urlDir;
    }
  }
  const forced = import.meta.env.VITE_FORCE_MORPH_DIRECTION;
  if (forced === "bowstring" || forced === "aimed-shot") {
    return forced;
  }
  return Math.random() < 0.5 ? "bowstring" : "aimed-shot";
}

/**
 * Test hook (Task 080): VITE_FAKE_ANSWER_DELAY_MS adds a client-side floor to
 * the in-flight time so the drawing/skeleton/pending states can be observed
 * deterministically in local dev. Test-only — unset/0 in production. It never
 * touches the network or the /answer contract; it only widens the loading
 * window the same way MIN_DRAW_MS already does.
 */
export function readFakeAnswerDelayMs(): number {
  const raw = import.meta.env.VITE_FAKE_ANSWER_DELAY_MS;
  const parsed = typeof raw === "string" ? Number.parseInt(raw, 10) : 0;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export function readAnswerUrl(): string {
  const value = import.meta.env.VITE_ANSWER_URL;
  return typeof value === "string" && value.length > 0
    ? value
    : import.meta.env.DEV ? "http://localhost:8787/answer" : "/answer";
}

/**
 * Streaming endpoint (Task 137). Explicit VITE_ANSWER_STREAM_URL wins; otherwise
 * derive `${VITE_ANSWER_URL}/stream`. The RAG (Task 135) and the proxy (Task 136)
 * both mount the SSE route directly under /answer, so the same derivation works
 * for the direct-dev path and the proxied path.
 */
export function readAnswerStreamUrl(): string {
  const explicit = import.meta.env.VITE_ANSWER_STREAM_URL;
  if (typeof explicit === "string" && explicit.length > 0) return explicit;
  const base = readAnswerUrl().replace(/\/+$/, "");
  return `${base}/stream`;
}

/**
 * Reader feedback (Task 251 Layer 2): `${VITE_ANSWER_URL}/feedback`. Only the
 * proxy serves it (production `/answer*` goes through Caddy to the proxy); the
 * direct-RAG dev path has no feedback route, so the controls report an error.
 */
export function readFeedbackUrl(): string {
  const base = readAnswerUrl().replace(/\/+$/, "");
  return `${base}/feedback`;
}

/**
 * Streaming is the default experience (Task 133/137). Set VITE_ANSWER_STREAMING
 * to "false"/"0" to fall back to the non-streaming postAnswer() path (kept for
 * environments without an SSE endpoint).
 */
export function readStreamingEnabled(): boolean {
  const raw = import.meta.env.VITE_ANSWER_STREAMING;
  return raw !== "false" && raw !== "0";
}

/**
 * Task 170 (condense-then-answer): follow-up rewriting ships DARK on the FE too.
 * When VITE_FOLLOWUP_ENABLED is "true"/"1", a follow-up submit sends a small
 * window of prior SETTLED turns so the server can rewrite an elliptical question
 * ("¿y el siguiente?") into a standalone one. Default OFF — the FE then posts
 * `{query}` exactly as before and no context ever leaves the browser. Must be
 * paired with CAMINO_REWRITE_ENABLED on the RAG; either side off == no rewrite.
 */
export function readFollowUpEnabled(): boolean {
  const raw = import.meta.env.VITE_FOLLOWUP_ENABLED;
  return raw === "true" || raw === "1";
}
